"use client";
import React, { useState } from "react";
import { motion } from "framer-motion";

/**
 * ProductCard
 * Props:
 *  - product: { image: string, name: string, price: string }
 *  - fit?: "cover" | "contain" (default "cover")
 *  - ratio?: "square" | "4/3" | "16/9" (default "square")
 *
 * Notes:
 *  - "cover" removes white space but may crop.
 *  - "contain" shows full image but may letterbox (white bars).
 */
const ProductCard = ({ product, fit = "cover", ratio = "square" }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Aspect ratio class for the image box
  const ratioClass =
    ratio === "square" ? "aspect-square"
    : ratio === "16/9"     ? "aspect-[16/9]"
    :                       "aspect-[4/3]";

  // Image fit behavior
  const fitClass = fit === "contain" ? "object-contain" : "object-cover";

  return (
    <motion.article
      className="flex overflow-hidden flex-col mx-auto w-full max-w-[320px] md:max-w-[350px] lg:max-w-[380px] h-full bg-white rounded-3xl border-[3px] border-slate-500 hover:border-red-500 transition-all duration-300 relative group cursor-pointer shadow-lg hover:shadow-2xl"
      whileHover={{ scale: 1.03, y: -8, rotateY: 3, boxShadow: "0 30px 60px rgba(0,0,0,0.2)" }}
      transition={{ duration: 0.3 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
    >
      {/* Floating decoration emojis */}
      <motion.div className="absolute -top-3 -left-3 text-3xl pointer-events-none z-20"
        initial={{ opacity: 0, scale: 0, rotate: 0 }}
        animate={isHovered ? { opacity: 1, scale: 1, rotate: 360, y: [0, -15, 0] } : { opacity: 0, scale: 0 }}
        transition={{ duration: 0.5 }}>🍓</motion.div>

      <motion.div className="absolute -top-3 -right-3 text-2xl pointer-events-none z-20"
        initial={{ opacity: 0, scale: 0, rotate: 0 }}
        animate={isHovered ? { opacity: 1, scale: 1, rotate: -360, y: [0, -20, 0] } : { opacity: 0, scale: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}>🍫</motion.div>

      <motion.div className="absolute -bottom-3 -left-3 text-xl pointer-events-none z-20"
        initial={{ opacity: 0, scale: 0, rotate: 0 }}
        animate={isHovered ? { opacity: 1, scale: 1, rotate: 180, y: [0, -12, 0] } : { opacity: 0, scale: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}>🥜</motion.div>

      <motion.div className="absolute -bottom-3 -right-3 text-2xl pointer-events-none z-20"
        initial={{ opacity: 0, scale: 0, rotate: 0 }}
        animate={isHovered ? { opacity: 1, scale: 1, rotate: -180, y: [0, -18, 0] } : { opacity: 0, scale: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}>🍯</motion.div>

      {/* Sparkle effects */}
      <motion.div className="absolute top-6 right-6 text-yellow-400 pointer-events-none z-20"
        animate={isHovered ? { scale: [1, 1.5, 1], rotate: [0, 180, 360], opacity: [0.5, 1, 0.5] } : { scale: 0, opacity: 0 }}
        transition={{ duration: 1, repeat: Infinity }}>✨</motion.div>

      <motion.div className="absolute bottom-6 right-6 text-yellow-400 pointer-events-none z-20"
        animate={isHovered ? { scale: [1, 1.2, 1], rotate: [0, -180, -360], opacity: [0.3, 0.8, 0.3] } : { scale: 0, opacity: 0 }}
        transition={{ duration: 1.2, repeat: Infinity, delay: 0.3 }}>⭐</motion.div>

      <motion.div className="absolute top-1/2 left-6 text-pink-400 pointer-events-none z-20"
        animate={isHovered ? { scale: [1, 1.3, 1], rotate: [0, 90, 180], opacity: [0.4, 0.9, 0.4] } : { scale: 0, opacity: 0 }}
        transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}>💫</motion.div>

      {/* IMAGE BOX — no letterboxing with object-cover */}
      <div className={`relative ${ratioClass} overflow-hidden rounded-t-2xl`}>
        {/* Subtle hover gradient */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent z-10"
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3 }}
        />

        <motion.img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`absolute inset-0 w-full h-full ${fitClass} object-center block transition-transform duration-500 group-hover:scale-110`}
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.5 }}
        />

        {/* SALE badge */}
        <motion.div
          className="absolute left-3 top-3 flex justify-center items-center px-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-3xl h-[50px] w-[50px] md:h-[55px] md:w-[55px] text-sm font-bold z-20 shadow-xl text-white"
          whileHover={{ rotate: 360, scale: 1.2, boxShadow: "0 15px 30px rgba(239,68,68,0.5)" }}
          transition={{ duration: 0.5 }}
          animate={{
            boxShadow: [
              "0 0 0 0 rgba(239,68,68,0.4)",
              "0 0 0 15px rgba(239,68,68,0)",
              "0 0 0 0 rgba(239,68,68,0)"
            ]
          }}
          style={{ animationDuration: "2s", animationIterationCount: "infinite" }}
        >
          <span className="font-bold">SALE</span>
        </motion.div>

        {/* Hover glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-t from-red-500/20 via-orange-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Product Info */}
      <motion.div
        className="flex gap-4 md:gap-6 justify-between items-start self-center px-4 py-6 w-full"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <motion.h3
          className="text-xl md:text-2xl leading-tight text-cyan-900 flex-1 min-h-[3.25rem] flex items-center font-semibold"
          style={{ fontFamily: "'Foolish Talk', cursive" }}
          whileHover={{ color: "#dc2626" }}
          transition={{ duration: 0.2 }}
        >
          {product.name}
        </motion.h3>
        <motion.span
          className="text-2xl md:text-3xl font-bold text-red-500 whitespace-nowrap flex-shrink-0 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent"
          whileHover={{ scale: 1.1, textShadow: "0 0 12px rgba(239,68,68,0.6)" }}
          transition={{ duration: 0.2 }}
        >
          {product.price}
        </motion.span>
      </motion.div>

      {/* Bottom gradient bar */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-red-500 via-orange-500 to-red-500 rounded-b-2xl"
        initial={{ scaleX: 0 }}
        animate={isHovered ? { scaleX: 1 } : { scaleX: 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Pulsing border */}
      <motion.div
        className="absolute inset-0 rounded-3xl border-2 border-transparent"
        animate={isHovered ? {
          borderColor: ["rgba(239,68,68,0)", "rgba(239,68,68,0.5)", "rgba(239,68,68,0)"],
        } : {}}
        transition={{ duration: 1.5, repeat: Infinity }}
      />
    </motion.article>
  );
};

export default ProductCard;
